import React, { useState, useEffect } from 'react';
import { MetricCard, Card, CardTitle, Grid, Insight, Btn } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function fmt(n) { return n === null || n === undefined ? '\u2026' : n.toLocaleString(); }
function pct(a, b) { return b ? ((a / b) * 100).toFixed(1) + '%' : '0%'; }

function Skel({ h = 12, mb = 10 }) {
  return (
    <div style={{ height: h, borderRadius: 6, marginBottom: mb,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite' }} />
  );
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}
      style={{ fontSize: '.6rem', padding: '2px 8px', borderRadius: 5, border: '.5px solid #d0dde7',
        background: copied ? '#eaf7f2' : '#f5f7f9', color: copied ? '#0d6640' : '#6F8FA7',
        cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .15s' }}>
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

export default function UTMReferral() {
  const [links,   setLinks]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await supabase.rpc('admin_get_utm_links');
        if (!cancelled) setLinks(data || []);
      } catch (_e) {
        try {
          const { data } = await supabase
            .from('utm_links')
            .select('id,label,source,medium,campaign,url,clicks,signups,created_at')
            .order('created_at', { ascending: false });
          if (!cancelled) setLinks(data || []);
        } catch (_e2) { console.error('UTM links fallback error:', _e2); }
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const totalClicks  = links.reduce((s, l) => s + (l.clicks || 0), 0);
  const totalSignups = links.reduce((s, l) => s + (l.signups || 0), 0);
  const convRate     = totalClicks ? ((totalSignups / totalClicks) * 100).toFixed(1) : '0';

  const MEDIUM_COLORS = {
    organic: '#314F5C', paid: '#d97706', social: '#6F8FA7',
    email: '#2da06a', referral: '#9b59b6', direct: '#9eb8c8',
  };

  return (
    <div style={{ fontFamily: F }}>
      <style>{'@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}'}</style>

      <Grid cols={4}>
        <MetricCard label="Total links"    value={loading ? '\u2026' : fmt(links.length)}
          accent="#314F5C" />
        <MetricCard label="Total clicks"   value={loading ? '\u2026' : fmt(totalClicks)}
          trend={totalClicks > 0 ? 'up' : null} accent="#6F8FA7" />
        <MetricCard label="Total signups"  value={loading ? '\u2026' : fmt(totalSignups)}
          trend={totalSignups > 0 ? 'up' : null} accent="#2da06a" />
        <MetricCard label="Conversion rate" value={loading ? '\u2026' : convRate + '%'}
          sub={!loading ? totalClicks + ' clicks \u2192 ' + totalSignups + ' signups' : undefined}
          accent={parseFloat(convRate) > 5 ? '#2da06a' : '#d97706'} />
      </Grid>

      <Card>
        <CardTitle>UTM links</CardTitle>
        {loading ? [1,2,3,4].map(i => <Skel key={i} h={52} mb={8} />) : (
          links.length === 0 ? (
            <Insight type="info">
              No UTM links yet. Create links to track traffic sources from email campaigns,
              social posts, and partnerships.
            </Insight>
          ) : (
            <div style={{ overflowX: 'auto' }}><div style={{ minWidth: 560 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 80px',
                gap: 8, padding: '6px 0', borderBottom: '1px solid #e9eef2',
                fontSize: '.6rem', fontWeight: 600, color: '#9eb8c8',
                textTransform: 'uppercase', letterSpacing: '.06em' }}>
                <div>Label</div><div>Source</div><div>Clicks</div>
                <div>Signups</div><div>Conv.</div><div>URL</div>
              </div>
              {links.map(link => {
                const conv = link.clicks ? ((link.signups || 0) / link.clicks * 100).toFixed(1) : '0';
                const mColor = MEDIUM_COLORS[(link.medium || '').toLowerCase()] || '#9eb8c8';
                return (
                  <div key={link.id} style={{ display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 80px',
                    gap: 8, padding: '9px 0', borderBottom: '.5px solid rgba(49,79,92,.04)',
                    fontSize: '.7rem', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 500, color: '#0f1e28' }}>{link.label}</div>
                      <div style={{ fontSize: '.6rem', color: '#9eb8c8', marginTop: 1 }}>
                        {link.campaign}
                      </div>
                    </div>
                    <div>
                      <span style={{ padding: '1px 7px', borderRadius: 999, fontSize: '.62rem',
                        fontWeight: 600, background: mColor + '18', color: mColor }}>
                        {link.source}
                      </span>
                    </div>
                    <div style={{ fontWeight: 600, color: '#0f1e28' }}>{fmt(link.clicks || 0)}</div>
                    <div style={{ fontWeight: 600, color: '#2da06a' }}>{fmt(link.signups || 0)}</div>
                    <div style={{ color: parseFloat(conv) > 5 ? '#2da06a' : '#6F8FA7',
                      fontWeight: parseFloat(conv) > 5 ? 600 : 400 }}>{conv}%</div>
                    <div>
                      <CopyBtn text={link.url || ''} />
                    </div>
                  </div>
                );
              })}
            </div></div>
          )
        )}
      </Card>
    </div>
  );
}
